import { Permission, Role } from '@ekkogmbh/apisdk';
import { Box, BoxProps, SelectProps, Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GenericDialog } from '../../Common/Components/GenericDialog';
import PaginatedSelect from 'src/Common/Components/Forms/PaginatedSelect';
import { injectFakePagination } from 'src/Common/Helper/Pagination';
import { ReactiveRow } from 'src/Common/Components/Stacks';

interface Props {
  role: Partial<Role>;
  allPermissions: Permission[];
  saveHandler: (role: Partial<Role>) => void;
  noDialog?: boolean;
  label?: string;
  width?: BoxProps['width'];
  onOpen?: () => void;
  onClose?: () => void;
}

export const RolePermissionSelect = ({
  role,
  allPermissions,
  saveHandler,
  noDialog,
  label,
  width,
  onOpen,
  onClose,
  ...props
}: Props & Omit<SelectProps, 'role' | 'multiple' | 'onChange' | 'value' | 'label' | 'onClose'>): React.JSX.Element => {
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [selectKey, setSelectKey] = useState(0);
  const rolePermissions = useMemo(() => role.permissions ?? [], [role]);

  const dismiss = useCallback(() => {
    setDialogOpen(false);
    setSelectedPermissions(rolePermissions);
    // Force PaginatedSelect to remount so it re-syncs with the reset value.
    // Needed because PaginatedSelect doesn't react to external value changes.
    setSelectKey((k) => k + 1);
  }, [rolePermissions]);

  const confirm = useCallback(() => {
    saveHandler({ ...role, permissions: selectedPermissions });
  }, [saveHandler, role, selectedPermissions]);

  useEffect(() => {
    dismiss();
  }, [rolePermissions]);

  const sortedPermissions = useMemo(() => [...allPermissions].sort((a, b) => a.localeCompare(b)), [allPermissions]);

  const dialog = useMemo(
    () => (
      <GenericDialog
        type={'confirmation'}
        open={isDialogOpen}
        title={
          <>
            {'Update Permissions of Role: '}
            <Box component={'span'} sx={{ fontWeight: 100, fontStyle: 'italic' }}>
              {role?.name ?? ''}
            </Box>
          </>
        }
        text={(() => {
          const allPerms = [...new Set([...rolePermissions, ...selectedPermissions])].sort((a, b) =>
            a.localeCompare(b),
          );
          const removed = new Set(rolePermissions.filter((p) => !selectedPermissions.includes(p)));
          const added = new Set(selectedPermissions.filter((p) => !rolePermissions.includes(p)));

          return (
            <ReactiveRow>
              <Stack direction={'column'}>
                <Typography variant={'button'} gutterBottom>
                  Before
                </Typography>
                {allPerms.map((p) => (
                  <Typography
                    key={p}
                    sx={{
                      ...(removed.has(p) && { color: 'error.main', textDecoration: 'line-through' }),
                      ...(added.has(p) && { visibility: 'hidden' }),
                    }}
                  >
                    {p}
                  </Typography>
                ))}
              </Stack>
              <Stack direction={'column'}>
                <Typography variant={'button'} gutterBottom>
                  After
                </Typography>
                {allPerms.map((p) => (
                  <Typography
                    key={p}
                    sx={{
                      ...(added.has(p) && { color: 'success.main', fontWeight: 600 }),
                      ...(removed.has(p) && { visibility: 'hidden' }),
                    }}
                  >
                    {p}
                  </Typography>
                ))}
              </Stack>
            </ReactiveRow>
          );
        })()}
        onClose={dismiss}
        onConfirm={() => {
          confirm();
        }}
      />
    ),
    [role, selectedPermissions, isDialogOpen, confirm],
  );

  const isChanged = useCallback(
    (p: string) => {
      const isSelected = selectedPermissions.includes(p);
      const isNew = !rolePermissions.includes(p);

      return isNew && isSelected;
    },
    [selectedPermissions, rolePermissions],
  );

  return (
    <Box width={width ?? 600}>
      {noDialog !== true && dialog}
      <PaginatedSelect
        key={selectKey}
        {...props}
        multiple
        reloadable={false}
        label={label ?? ''}
        value={selectedPermissions}
        onChange={(items) => setSelectedPermissions((items as string[]) ?? [])}
        // FIXME: PaginatedSelect with `multiple` is broken — selected items get
        // lost across pages and the reservedItems mechanism doesn't handle it
        // correctly. Rewrite PaginatedSelect to properly support multi-select
        // with pagination. Until then, return all items on a single page.
        fetchPaginatedItems={async () => injectFakePagination(sortedPermissions)}
        itemId={(i) => i}
        itemRender={(i) => (isChanged(i) ? '*' : '') + i}
        onOpen={onOpen}
        onClose={() => {
          onClose?.();
          const hasChanges =
            selectedPermissions.length !== rolePermissions.length ||
            [...selectedPermissions].sort().join(',') !== [...rolePermissions].sort().join(',');
          if (!hasChanges) {
            return;
          }
          if (noDialog === true) {
            confirm();
          } else {
            setDialogOpen(true);
          }
        }}
      />
    </Box>
  );
};
